import React, { useCallback, useEffect, useRef, useState } from 'react';
import {
	CardElement,
	PaymentElement,
	useElements,
	useStripe,
} from '@stripe/react-stripe-js';
import styled from 'styled-components';
import getAmountLabel from '../../utils/getAmountLabel';
import dictionary from '../../utils/dictionary';
import { waitForPaymentIntentSucceeded } from '../../utils/waitForPaymentIntentSucceeded';
import { confirmDeferredPayment } from '../../utils/confirmDeferredPayment';
import {
	getPaymentIntentExpandedForMethodCheck,
	isUsBankAccountPaymentIntent,
	isAcssDebitPaymentIntent,
} from '../../utils/paymentIntentMethod';
import { debugPaymentsLog } from '../../utils/debugPayments';
import { LockIcon } from './icons';
import { getOrderedCheckoutMethods } from '../../utils/checkoutPaymentMethodOrder';

const acceptableLanguages = ['en', 'fr', 'es'];

const CARD_ELEMENT_STYLE = {
	base: {
		color: '#32325d',
		fontFamily: '"Helvetica Neue", Helvetica, sans-serif',
		fontSmoothing: 'antialiased',
		fontSize: '16px',
		'::placeholder': {
			color: '#aab7c4',
		},
	},
	invalid: {
		color: '#fa755a',
		iconColor: '#fa755a',
	},
};

const CardElementContainer = styled.div`
	padding: 12px;
	border: 1px solid #e2e8f0;
	border-radius: 8px;
	background: #ffffff;

	@media (max-width: 480px) {
		padding: 10px;
		border-radius: 6px;
	}
`;

const FormContainer = styled.form`
	width: 100%;
	display: flex;
	flex-direction: column;
	justify-content: flex-start;
	padding: 0;
`;

/** No second “card” frame — InvoiceContainer is already white; extra border looked nested / wrong. */
const FormStack = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	gap: 8px;
	margin: 4px 0 12px 0;

	@media (max-width: 480px) {
		margin: 2px 0 8px 0;
		gap: 6px;
	}
`;

/** Space before Pay — no extra rule line (Stripe already separates tabs from fields). */
const PostStripeSection = styled.div`
	display: flex;
	flex-direction: column;
	gap: 4px;
	padding-top: 8px;
	margin-top: 0;
`;

/** Always rendered with one line reserved under the Pay button, so an error doesn't resize the card. */
const ErrorMessage = styled.div`
	margin-top: 0;
	color: #dc2626;
	font-size: 14px;
	font-weight: 500;
	line-height: 20px;
	min-height: 20px;

	@media (max-width: 480px) {
		font-size: 13px;
		line-height: 18px;
		min-height: 18px;
	}
`;

const FinalizingMessage = styled.div`
	color: #64748b;
	font-size: 14px;
	font-weight: 500;
	margin-top: 8px;
	width: 100%;
	box-sizing: border-box;
	padding: 0 12px;
	text-align: left;

	@media (max-width: 480px) {
		font-size: 13px;
		margin-top: 6px;
	}
`;

const SubmitButton = styled.button`
	width: 100%;
	padding: 8px 24px;
	border: none;
	border-radius: 4px;
	font-size: 16px;
	font-weight: 600;
	font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif;
	cursor: pointer;
	transition: all 0.2s ease;
	display: flex;
	align-items: center;
	justify-content: center;
	gap: 4px;
	text-transform: none;
	box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
	min-height: 32px;
	background: #000000;
	color: white;

	@media (max-width: 480px) {
		padding: 6px 20px;
		font-size: 15px;
		border-radius: 4px;
		min-height: 32px;
		gap: 3px;
	}

	@media (max-width: 360px) {
		padding: 4px 16px;
		font-size: 14px;
		min-height: 28px;
	}

	&:hover {
		transform: translateY(-1px);
		box-shadow: 0 4px 8px rgba(0, 0, 0, 0.15);
		background: #1a1a1a;
	}

	@media (hover: none) {
		&:hover {
			transform: none;
			box-shadow: 0 2px 4px rgba(0, 0, 0, 0.1);
		}
	}

	&:active {
		transform: translateY(0);
		background: #000000 !important;
	}

	&:focus {
		background: #000000;
		outline: none;
	}

	&:disabled {
		opacity: 0.6;
		cursor: not-allowed;
		transform: none;
	}
`;

const SubmitArea = styled.div`
	width: 100%;
	display: flex;
	flex-direction: column;
	align-items: stretch;
`;

// Reserve stable space so button position never jumps when message appears.
const FinalizingSlot = styled.div`
	min-height: ${(p) => (p.$showFinalizing ? '44px' : '0')};
	width: 100%;
	display: flex;
	align-items: flex-start;
	justify-content: center;

	@media (max-width: 480px) {
		min-height: ${(p) => (p.$showFinalizing ? '40px' : '0')};
	}
`;

const CheckoutForm = ({
	amount,
	currency,
	brandColor,
	clientSecret,
	customerName,
	customerEmail,
	country,
	disabled,
	onCheckoutSubmit,
	onPaymentIntentUpdated,
	language,
	checkoutPaymentMethodTypes,
	suppressPaymentElementWallets,
}) => {
	const [error, setError] = useState(null);
	const [success, setSuccess] = useState(null);
	const [isPaymentElementComplete, setIsPaymentElementComplete] = useState(false);
	const [finalizingPayment, setFinalizingPayment] = useState(false);
	const [submitting, setSubmitting] = useState(false);
	const pollActiveRef = useRef(true);
	const stripe = useStripe();

	const elements = useElements();

	useEffect(() => {
		pollActiveRef.current = true;
		return () => {
			pollActiveRef.current = false;
		};
	}, []);

	// When the PaymentIntent changes (client secret changes), Stripe remounts the
	// PaymentElement. Reset completion so the Pay button doesn't stay enabled from a
	// previous method.
	useEffect(() => {
		setIsPaymentElementComplete(false);
	}, [clientSecret]);

	const dict = dictionary[acceptableLanguages.includes(language) ? language : 'en'];
	const { paymentSuccessTitle, paymentSuccessBody, pay } = dict;

	const completeIfSucceeded = useCallback((paymentIntent) => {
		if (!paymentIntent || paymentIntent.status !== 'succeeded') {
			return false;
		}
		if (onPaymentIntentUpdated) {
			onPaymentIntentUpdated(paymentIntent);
		}
		setSuccess(true);
		onCheckoutSubmit({
			success: true,
			message: paymentSuccessTitle + paymentSuccessBody,
		});
		return true;
	}, [
		onPaymentIntentUpdated,
		onCheckoutSubmit,
		paymentSuccessBody,
		paymentSuccessTitle,
	]);

	// The refresh-restore effect below reads these through refs so a parent re-render
	// (new onCheckoutSubmit) doesn't restart its poll.
	const completeIfSucceededRef = useRef(completeIfSucceeded);
	completeIfSucceededRef.current = completeIfSucceeded;

	const completeIfPendingBank = useCallback((paymentIntent, bankKind) => {
		if (onPaymentIntentUpdated && paymentIntent) {
			onPaymentIntentUpdated(paymentIntent);
		}
		setSuccess(true);
		onCheckoutSubmit({ pendingBank: bankKind });
	}, [onPaymentIntentUpdated, onCheckoutSubmit]);
	const completeIfPendingBankRef = useRef(completeIfPendingBank);
	completeIfPendingBankRef.current = completeIfPendingBank;

	function getPaymentFailureMessage(pi) {
		if (!pi || typeof pi !== 'object') {
			return 'Payment could not be completed. Please try again.';
		}
		const lastErr = pi.last_payment_error;
		if (lastErr && typeof lastErr === 'object' && lastErr.message) {
			return lastErr.message;
		}
		// Stripe sometimes returns a non-success status without `result.error`.
		switch (pi.status) {
			case 'requires_payment_method':
				return 'Payment was not completed. Please review your payment details and try again.';
			case 'requires_action':
				return 'Additional steps are required to complete this payment. Please try again.';
			case 'canceled':
				return 'Payment was canceled. Please try again.';
			default:
				return 'Payment could not be completed. Please try again.';
		}
	}

	// If the user refreshes during a bank-flow “processing” period, restore the
	// correct UI state so they can't re-enter payment details again.
	useEffect(() => {
		let cancelled = false;
		(async () => {
			if (!stripe || !clientSecret) {
				return;
			}

			const { paymentIntent: retrieved, error } = await stripe.retrievePaymentIntent(
				clientSecret,
				{
					expand: ['payment_method'],
				}
			);

			if (cancelled || error || !retrieved) {
				return;
			}

			// Only restore the “bank is still processing” UI. Success is already
			// handled by the parent `Invoice` render.
			if (retrieved.status === 'processing') {
				// US ACH stays pending for days — show the pending banner.
				if (isUsBankAccountPaymentIntent(retrieved)) {
					completeIfPendingBankRef.current(retrieved, 'ach');
					return;
				}
				// ACSS/PAD can take days — show pending banner.
				if (isAcssDebitPaymentIntent(retrieved)) {
					completeIfPendingBankRef.current(retrieved, 'acss');
					return;
				}

				setFinalizingPayment(true);
				try {
					const outcome = await waitForPaymentIntentSucceeded(
						stripe,
						clientSecret,
						{
							shouldAbort: () => cancelled || !pollActiveRef.current,
						},
					);
					if (outcome.aborted) {
						return;
					}
					if (outcome.error) {
						setError(outcome.error.message);
					} else if (outcome.failed) {
						setError('Payment was not completed. Please try again.');
					} else if (outcome.timeout) {
						setError(
							'Payment is still processing. Please refresh this page in a moment.',
						);
					} else if (outcome.paymentIntent) {
						completeIfSucceededRef.current(outcome.paymentIntent);
					}
				} finally {
					if (!cancelled && pollActiveRef.current) {
						setFinalizingPayment(false);
					}
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [stripe, clientSecret]);

	const normalizedName =
		typeof customerName === 'string' && customerName.trim() !== ''
			? customerName.trim()
			: undefined;
	// Customer records can hold placeholders like "N/A"; Stripe rejects those as invalid.
	const normalizedEmail =
		typeof customerEmail === 'string' &&
		/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(customerEmail.trim())
			? customerEmail.trim()
			: undefined;

	// The business's country stands in for the billing country so the Payment
	// Element can drop its Country dropdown; Stripe requires it on confirm when hidden.
	const billingCountry =
		typeof country === 'string' && /^[a-z]{2}$/i.test(country.trim())
			? country.trim().toUpperCase()
			: undefined;

	const orderedMethodIds = getOrderedCheckoutMethods(
		checkoutPaymentMethodTypes,
	);
	// Card-only checkout uses the single-line CardElement (it collects ZIP/postal code
	// inline). The Payment Element is only needed once bank debit is offered.
	const useCardLine =
		orderedMethodIds.length === 1 && orderedMethodIds[0] === 'card';
	const cardElementOptions = {
		style: CARD_ELEMENT_STYLE,
		disabled: finalizingPayment || submitting,
	};
	const paymentElementOptions = {
		defaultValues: {
			billingDetails: {
				name: normalizedName,
				email: normalizedEmail,
			},
		},
		...(billingCountry
			? { fields: { billingDetails: { address: { country: 'never' } } } }
			: {}),
		// Tabs even for a single method: the default layout adds a "Card" heading and indent.
		layout: { type: 'tabs' },
		paymentMethodOrder: orderedMethodIds,
		// Link is off; in deferred mode Stripe otherwise adds its "save my information" sign-up under the card fields.
		wallets: {
			link: 'never',
			...(suppressPaymentElementWallets
				? { applePay: 'never', googlePay: 'never' }
				: {}),
		},
		...(finalizingPayment ? { readOnly: true } : {}),
	};

	// Handle form submission for the PaymentElement.
	const handleSubmit = async (event) => {
		event.preventDefault();

		if (!stripe || !elements) {
			return;
		}

		if (submitting || finalizingPayment) {
			return;
		}

		setSubmitting(true);

		try {
			if (useCardLine) {
				const cardResult = await stripe.confirmCardPayment(clientSecret, {
					payment_method: {
						card: elements.getElement(CardElement),
						billing_details: {
							name: normalizedName,
							email: normalizedEmail,
						},
					},
				});
				if (cardResult.error) {
					debugPaymentsLog('payments:confirmCardPayment error', cardResult.error);
					setError(
						cardResult.error.message ||
							'Payment could not be completed. Please try again.',
					);
					setSuccess(null);
					return;
				}
				setError(null);
				if (completeIfSucceeded(cardResult.paymentIntent)) {
					return;
				}
				setError(getPaymentFailureMessage(cardResult.paymentIntent));
				setSuccess(null);
				return;
			}

			const submitResult = await elements.submit();
			if (submitResult && submitResult.error) {
				debugPaymentsLog('payments:elements.submit error', submitResult.error);
				if (submitResult.error.code === 'incomplete_payment_details') {
					setError(
						'Please complete all required payment fields. For pre-authorized debit, make sure account/authorization details are completed and allow pop-ups if your browser blocks bank verification.',
					);
				} else {
					setError(
						submitResult.error.message ||
							'Payment details could not be submitted. Please try again.',
					);
				}
				setSuccess(null);
				return;
			}

			const result = await confirmDeferredPayment({
				stripe,
				elements,
				clientSecret,
				billingDetails: {
					name: normalizedName,
					email: normalizedEmail,
					...(billingCountry ? { address: { country: billingCountry } } : {}),
				},
			});

			if (result.error) {
				debugPaymentsLog('payments:confirmPayment error', result.error);
				// Inform the user if there was an error.
				setError(
					result.error.message ||
						'Payment could not be completed. Please try again.',
				);
				setSuccess(null);
				return;
			}

			setError(null);
			const pi = result.paymentIntent;

			if (completeIfSucceeded(pi)) {
				return;
			}

			// US ACH often stays `processing` for days — show pending messaging instead of polling to success.
			if (pi && pi.status === 'processing') {
				const piForMethod = await getPaymentIntentExpandedForMethodCheck(
					stripe,
					clientSecret,
					pi,
				);
				if (isUsBankAccountPaymentIntent(piForMethod)) {
					completeIfPendingBank(piForMethod, 'ach');
					return;
				}
				if (isAcssDebitPaymentIntent(piForMethod)) {
					completeIfPendingBank(piForMethod, 'acss');
					return;
				}
			}

			// ACSS / other bank flows: confirm often returns `processing` until verification completes.
			if (pi && pi.status === 'processing') {
				setFinalizingPayment(true);
				try {
					const outcome = await waitForPaymentIntentSucceeded(
						stripe,
						clientSecret,
						{
							shouldAbort: () => !pollActiveRef.current,
						},
					);

					if (outcome.aborted) {
						return;
					}
					if (outcome.error) {
						setError(outcome.error.message);
						return;
					}
					if (outcome.failed) {
						setError('Payment was not completed.');
						return;
					}
					if (outcome.timeout) {
						const lastPi = await getPaymentIntentExpandedForMethodCheck(
							stripe,
							clientSecret,
							outcome.paymentIntent || pi,
						);
						if (lastPi && lastPi.status === 'processing') {
							if (isUsBankAccountPaymentIntent(lastPi)) {
								completeIfPendingBank(lastPi, 'ach');
								return;
							}
							if (isAcssDebitPaymentIntent(lastPi)) {
								completeIfPendingBank(lastPi, 'acss');
								return;
							}
						}
						setError(
							'Payment is still processing. Please refresh this page in a moment.',
						);
						return;
					}
					if (outcome.paymentIntent) {
						completeIfSucceeded(outcome.paymentIntent);
					}
				} finally {
					setFinalizingPayment(false);
				}
				return;
			}

			// If we got here, Stripe did not return a succeeded/processing outcome.
			// Show an explicit error so the user isn't left on a “stuck” screen.
			setError(getPaymentFailureMessage(pi));
			setSuccess(null);
		} catch (err) {
			// eslint-disable-next-line no-console
			console.error('payments:checkout submit unhandled error', err);
			setError(
				process.env.NODE_ENV === 'development' && err && err.message
					? err.message
					: 'Payment could not be completed. Please try again.',
			);
			setSuccess(null);
		} finally {
			setSubmitting(false);
		}
	};

	if (success) {
		return <div />;
	}

	return (
		<FormContainer onSubmit={handleSubmit}>
			<FormStack>
				{useCardLine ? (
					<CardElementContainer>
						<CardElement
							key={clientSecret}
							id='card-element'
							options={cardElementOptions}
							onChange={(event) => {
								setIsPaymentElementComplete(Boolean(event && event.complete));
								setError(event && event.error ? event.error.message : null);
							}}
						/>
					</CardElementContainer>
				) : (
					<PaymentElement
						key={clientSecret}
						id='payment-element'
						options={paymentElementOptions}
						onChange={(event) => {
							setIsPaymentElementComplete(Boolean(event && event.complete));
							if (event && event.complete && error) {
								setError(null);
							}
						}}
					/>
				)}

				<PostStripeSection>
					<SubmitArea>
						<SubmitButton
							brandColor={brandColor}
							type='submit'
							disabled={
								disabled ||
								!stripe ||
								!isPaymentElementComplete ||
						finalizingPayment ||
						submitting
							}
						>
							<LockIcon />
							{pay} {getAmountLabel({ amount, currency })}
						</SubmitButton>
						<FinalizingSlot
							aria-live='polite'
							$showFinalizing={finalizingPayment}
						>
							{finalizingPayment ? (
								<FinalizingMessage role='status'>
									Finalizing payment… bank payments can take 1–5 business days to
									clear. You can leave this page and refresh later to see the final
									status.
								</FinalizingMessage>
							) : null}
						</FinalizingSlot>
					</SubmitArea>
				</PostStripeSection>

				<ErrorMessage role='alert'>{error}</ErrorMessage>
			</FormStack>
		</FormContainer>
	);
};

export default CheckoutForm;
